// View Component: Ana hero bölümü
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AuroraBackground from "../UI/AuroraBackground";
import Button from "../UI/Button";
import TiltCard from "../UI/TiltCard";
import { useSmoothScroll } from "../../hooks/useLandingPage";

const Hero = () => {
  const scrollToSection = useSmoothScroll();

  // Carousel için state'ler
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Mockup görselleri array'i
  const mockupImages = [
    "/mock_up_1.png",
    "/mock_up_2.png",
    "/mock_up_3.png",
    "/mock_up_4.png",
  ];

  // Otomatik geçiş efekti - hover durumunda durur
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(
        (prevIndex) => (prevIndex + 1) % mockupImages.length
      );
    }, 3000); // 3 saniyede bir değişir

    return () => clearInterval(interval);
  }, [isHovered, mockupImages.length]);

  const heroStyles = {
    minHeight: "100vh",
    background: "linear-gradient(135deg, var(--teal-50), var(--purple-50))",
    display: "flex",
    alignItems: "center",
    position: "relative",
    overflow: "hidden",
  };

  const containerStyles = {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 20px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "60px",
    alignItems: "center",
    width: "100%",
    zIndex: 2,
  };

  const contentStyles = {
    color: "var(--text-primary)",
    maxWidth: "500px",
  };

  const titleStyles = {
    fontSize: "clamp(2.5rem, 5vw, 4rem)",
    fontWeight: "700",
    lineHeight: "1.1",
    marginBottom: "24px",
    background:
      "linear-gradient(135deg, var(--primary-600) 0%, var(--secondary-500) 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  };

  const subtitleStyles = {
    fontSize: "1.25rem",
    color: "var(--text-secondary)",
    marginBottom: "32px",
    lineHeight: "1.6",
  };

  const buttonContainerStyles = {
    display: "flex",
    gap: "16px",
    flexWrap: "wrap",
  };

  const phoneContainerStyles = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
  };

  // Telefon çerçevesi stilleri
  const phoneFrameStyles = {
    width: "280px",
    height: "560px",
    background: "linear-gradient(145deg, #1f2937, #374151)",
    borderRadius: "40px",
    padding: "8px",
    boxShadow: `
      0 25px 50px -12px rgba(0, 0, 0, 0.25),
      0 0 0 1px rgba(255, 255, 255, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.1)
    `,
    position: "relative",
    transition: "all 0.3s ease",
  };

  // Telefon ekranı stilleri
  const phoneScreenStyles = {
    width: "100%",
    height: "100%",
    backgroundColor: "#000",
    borderRadius: "32px",
    overflow: "hidden",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  // Görsel stilleri
  const imageStyles = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "32px",
  };

  // Responsive stilleri
  const mediaQueries = `
    @media (max-width: 768px) {
      .hero-container {
        grid-template-columns: 1fr !important;
        gap: 40px !important;
        text-align: center;
      }
      
      .phone-frame {
        width: 240px !important;
        height: 480px !important;
      }
    }
  `;

  return (
    <AuroraBackground>
      <style>{mediaQueries}</style>
      <section style={heroStyles} id="home">
        <div className="hero-container" style={containerStyles}>
          {/* Sol taraf - İçerik */}
          <motion.div
            style={contentStyles}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.h1
              style={titleStyles}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Manage Your
              <br />
              Inbox with AI
            </motion.h1>

            <motion.p
              style={subtitleStyles}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Manage your inbox with AI, optimize your mails, and live a more
              focused life. Become more productive every day with personalized
              summaries and smart drafts.
            </motion.p>

            <motion.div
              style={buttonContainerStyles}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <Button
                variant="primary"
                size="large"
                onClick={() =>
                  window.open(
                    "https://apps.apple.com/us/app/inbox-zero-ai-mail-coach/id6747061594",
                    "_blank"
                  )
                }
              >
                Start Free
              </Button>
              <Button
                variant="outline"
                size="large"
                onClick={() =>
                  window.open(
                    "https://apps.apple.com/us/app/inbox-zero-ai-mail-coach/id6747061594",
                    "_blank"
                  )
                }
              >
                Download App
              </Button>
            </motion.div>
          </motion.div>

          {/* Sağ taraf - Telefon Mockup Carousel */}
          <motion.div
            style={phoneContainerStyles}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div
              onMouseEnter={() => {
                console.log("Mouse entered - stopping carousel");
                setIsHovered(true);
              }}
              onMouseLeave={() => {
                console.log("Mouse left - starting carousel");
                setIsHovered(false);
              }}
              style={{ display: "inline-block" }}
            >
              <TiltCard
                tiltMaxAngleX={12}
                tiltMaxAngleY={12}
                perspective={1000}
                scale={1.05}
                transitionSpeed={300}
                gyroscope={true}
                glareEnable={true}
                glareMaxOpacity={0.2}
                glareColor="#14b8a6"
                glarePosition="all"
                glareBorderRadius="40px"
                colorOverlay="rgba(20, 184, 166, 0.05)"
                style={{
                  borderRadius: "40px",
                  background: "transparent",
                  filter: "drop-shadow(0 20px 40px rgba(20, 184, 166, 0.15))",
                }}
              >
                <div className="phone-frame" style={phoneFrameStyles}>
                  <div style={phoneScreenStyles}>
                    <AnimatePresence mode="wait">
                      <motion.img
                        key={currentImageIndex}
                        src={mockupImages[currentImageIndex]}
                        alt={`Inbox Zero AI - Ekran ${currentImageIndex + 1}`}
                        style={imageStyles}
                        initial={{ opacity: 0, scale: 1.1 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{
                          duration: 0.6,
                          ease: "easeInOut",
                        }}
                      />
                    </AnimatePresence>
                  </div>
                </div>
              </TiltCard>
            </div>
          </motion.div>
        </div>
      </section>
    </AuroraBackground>
  );
};

export default Hero;
